import dayjs from "dayjs";

export type PlanType = "coordination" | "phase" | "tod" | "controller";

// Robust date-range overlap check (null/"infinity" treated as open)
export const toDayjsOrNull = (d?: string | null) => {
	if (!d || d === "infinity") return null;
	// Parse in UTC mode to avoid timezone shifts
	// If the input is an ISO timestamp like "2026-01-27T00:00:00.000Z", we want to extract "2026-01-27"
	// If the input is already a date string like "2026-01-27", we want to keep it as-is
	const parsed = dayjs.utc(d);
	return parsed.isValid() ? parsed : null;
};

// Canonical helper: strip any time/zone component and map infinity to null.
// This ensures every Optimus Configuration surface derives the date portion
// from valid_from/valid_to in exactly the same way.
export const extractDateOnly = (value?: string | null): string | null => {
	if (!value) return null;
	if (value === "infinity" || value === "-infinity") return null;
	const datePart = value.split("T")[0];
	return datePart || null;
};


export const doDateRangesOverlap = (
	start1: string | null | undefined,
	end1: string | null | undefined,
	start2: string | null | undefined,
	end2: string | null | undefined,
): boolean => {
	const s1 = toDayjsOrNull(start1);
	const e1 = toDayjsOrNull(end1);
	const s2 = toDayjsOrNull(start2);
	const e2 = toDayjsOrNull(end2);

	const range1Start = s1 || dayjs("1900-01-01");
	const range1End = e1 || dayjs("2100-12-31");
	const range2Start = s2 || dayjs("1900-01-01");
	const range2End = e2 || dayjs("2100-12-31");

	// Backend uses [valid_from, valid_to) semantics (exclusive end)
	// Two ranges [start1, end1) and [start2, end2) overlap if:
	// start1 < end2 AND start2 < end1
	//
	// Key insight: if end1 === start2, they DON'T overlap
	// Example: [2025-10-27, 2026-01-27) and [2026-01-27, infinity) don't overlap
	// because 2026-01-27 is NOT included in the first range (exclusive end)
	//
	// We need to use strict less-than (<) not less-than-or-equal (<=)
	// isBefore() returns true if date1 < date2 (strict), which is what we want

	const overlaps = range1Start.isBefore(range2End) && range2Start.isBefore(range1End);

	// DEBUG: Log overlap detection
	// console.log('🔍 DEBUG doDateRangesOverlap', {
	// 	start1, end1, start2, end2,
	// 	range1Start: range1Start.format('YYYY-MM-DD'),
	// 	range1End: range1End.format('YYYY-MM-DD'),
	// 	range2Start: range2Start.format('YYYY-MM-DD'),
	// 	range2End: range2End.format('YYYY-MM-DD'),
	// 	check1: `${range1Start.format('YYYY-MM-DD')} < ${range2End.format('YYYY-MM-DD')} = ${range1Start.isBefore(range2End)}`,
	// 	check2: `${range2Start.format('YYYY-MM-DD')} < ${range1End.format('YYYY-MM-DD')} = ${range2Start.isBefore(range1End)}`,
	// 	overlaps
	// });

	return overlaps;
};

// Logical identity per plan type
export const getLogicalKey = (plan: any, planType: PlanType): string => {
	if (planType === "coordination") {
		return String(plan?.coordination_settings_external_id || plan?.coordination_settings_id || "");
	}
	if (planType === "phase") {
		return String(plan?.phase_settings_external_id || plan?.phase_settings_id || "");
	}
	if (planType === "controller") {
		return String(plan?.controller_settings_external_id || plan?.controller_settings_id || "");
	}
	// TOD: match server semantics – uniqueness is per time_of_day_id (or local id for new rows)
	const timeOfDayData = plan.time_of_day_data || {};
	const daysString = `${timeOfDayData.days.sunday ? 1 : 0}${timeOfDayData.days.monday ? 1 : 0}${timeOfDayData.days.tuesday ? 1 : 0}${timeOfDayData.days.wednesday ? 1 : 0}${timeOfDayData.days.thursday ? 1 : 0}${timeOfDayData.days.friday ? 1 : 0}${timeOfDayData.days.saturday ? 1 : 0}`;
	const key = `${timeOfDayData.local_start_time}_${timeOfDayData.local_end_time}_${daysString}`;
	return key;
};

// Pure validator usable in tests by injecting existing plans
export const validateDateRangeWithPlans = (
	existingPlans: any[],
	startDate: string,
	endDate: string | null,
	currentPlanId: string,
	planType: PlanType,
	currentPlanData: any,
): string | null => {
	const ref = (currentPlanData && (currentPlanData.currentData || currentPlanData)) || {};
	const currentKey = getLogicalKey(ref, planType);
	const samePlanConfigs = (existingPlans || []).filter((plan) => getLogicalKey(plan, planType) === currentKey);


	for (const plan of samePlanConfigs) {
		let planId: string;
		switch (planType) {
			case "coordination":
				planId = plan.coordination_settings_id;
				break;
			case "phase":
				planId = plan.phase_settings_id;
				break;
			case "controller":
				planId = plan.controller_settings_id;
				break;
			default:
				planId = plan.time_of_day_id;
				break;
		}
		if (String(planId) === String(currentPlanId)) {
			continue;
		}

		const planStart = extractDateOnly(plan.valid_from);
		const planEnd = extractDateOnly(plan.valid_to);

		const overlaps = doDateRangesOverlap(startDate, endDate, planStart, planEnd);


		if (overlaps) {
			const planName =
				plan.coordination_settings_external_id ||
				plan.phase_settings_external_id ||
				plan.name ||
				`Plan ${planId}`;
			return `Date range overlaps with existing plan: ${planName}`;
		}
	}
	return null;
};


// Shared helper to compute the archive end date based on an upcoming plan start date.
// The archived plan ends on the SAME day the new plan starts (no gap, no -1 day).
// Both plans transition on the same date.
// If startDate is provided (YYYY-MM-DD), we return it as-is.
// If not provided, we fall back to "today" (current day).
// Returned value is a YYYY-MM-DD string in UTC.
export const computeArchiveEndDate = (startDate?: string | null): string => {
	if (startDate) {
		// Return the start date as-is (archived plan ends on same day new plan starts)
		return startDate;
	}

	// If no start date provided, use today
	const now = new Date();
	const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	return todayLocal.toISOString().split("T")[0];
};


export const createValidRange = (start_date: string, end_date: string | null) => {
	const toStartTS = (d: string) => `${d}T00:00:00.000Z`
	const toEndTS = (d?: string | null) => (d ? `${d}T23:59:59.999Z` : 'infinity')

	const valid_range_string = `["${toStartTS(start_date)}", "${end_date ? toEndTS(end_date) : 'infinity'}")`
	return valid_range_string
}